import { AnswerModel } from '@/components/decision-tree/hooks';
import useOnMount from '@/hooks/useOnMount';
import authService from '@/services/auth.service';
import { FocusEvent, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { EmailAndNameAnswer, EmailAndNameValidation } from '../Email/types';

type EmailAndNameControllerProps = {
  validationValues?: EmailAndNameValidation;
  saveAnswer: (payload: AnswerModel) => void;
  defaultValue?: EmailAndNameAnswer;
  isFirstNameRequired?: boolean;
};

export default function useEmailAndNameController({
  validationValues,
  saveAnswer,
  defaultValue,
  isFirstNameRequired = true,
}: EmailAndNameControllerProps) {
  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: defaultValue?.value.email || authService.getVisitorEmail() || '',
      firstName: defaultValue?.value.firstName || authService.getVisitorFirstName() || '',
    },
  });

  // If authService has previous values, save them to the form
  useOnMount(() => {
    if (authService.getVisitorEmail()) {
      saveAnswer({
        type: 'emailAndName',
        value: {
          type: 'emailAndName',
          email: authService.getVisitorEmail() ?? '',
          firstName: authService.getVisitorFirstName() ?? '',
        },
      });
    }
  });

  const validationOptions = {
    email: {
      required: 'Email is required',
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
    firstName: isFirstNameRequired
      ? {
          required: 'First name is required',
        }
      : {},
  };

  const onInputFn = (e: FormEvent<HTMLInputElement>) => {
    const currentTarget = e.currentTarget;
    form.setValue(currentTarget.name as 'email' | 'firstName', currentTarget.value);

    // Always save the full object if the email is valid
    const values = form.getValues();
    const email = currentTarget.name === 'email' ? currentTarget.value : values.email;
    const firstName = currentTarget.name === 'firstName' ? currentTarget.value : values.firstName || '';
    const emailRegex = validationOptions.email.pattern.value;
    if (email && emailRegex.test(email)) {
      saveAnswer({
        type: 'emailAndName',
        value: {
          type: 'emailAndName',
          email,
          firstName,
        },
      });
    }
  };

  const onBlurFn = (e: FocusEvent<HTMLInputElement>) => {
    const currentTarget = e.currentTarget;
    form.setValue(currentTarget.name as 'email' | 'firstName', currentTarget.value);

    // Always save the full object on blur
    const values = form.getValues();
    const email = currentTarget.name === 'email' ? currentTarget.value : values.email;
    const firstName = currentTarget.name === 'firstName' ? currentTarget.value : values.firstName || '';
    saveAnswer({
      type: 'emailAndName',
      value: {
        type: 'emailAndName',
        email,
        firstName,
      },
    });
  };

  return { onInputFn, form, validationOptions, onBlurFn };
}
